<script context="module" lang="ts">
	import Section from '$lib/components/layout/Section.svelte';
	import DirectusMediaSlider from '$lib/components/util/DirectusMediaSlider.svelte';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';
	import Slider from '$lib/components/util/Slider.svelte';
	import Media from '$lib/components/media/Media.svelte';
	import { onMount, onDestroy, afterUpdate } from 'svelte';

	export const config: SectionConfig = {
		collection: 'section_gallery',
		availableFor: ['pages', 'posts'],
		query: gql`
			id
			keep_aspect_ratio
			slides {
				media {
					id
					type
					translations {
						languages_code {
								code
						}
						media_description
					}
				}
				translations {
					languages_code {
							code
					}
					title
					text
				}
			}
    `
	};

	type Props = {
		items: any[];
		id: string;
		slides: Array<any>;
		keep_aspect_ratio: boolean;
	};
</script>

<script lang="ts">
	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)

	$: slides = [...data.slides, ...data.slides];

	let options = {
		autoplay: {
			delay: 500000,
			disableOnInteraction: false
		}
	};

	if (data.keep_aspect_ratio) {
		options['slidesPerView'] = 'auto';
		options['beakpoints'] = {};
	}

	function updateHeight() {
		let elements = document.querySelectorAll(`.gallery-${data.id} .swiper-slide .description`);
		let maxHeight = 0;
		for (const element of elements) {
			if (element.scrollHeight > maxHeight) {
				maxHeight = element.scrollHeight;
			}
		}

		maxHeight += 16;

		let galleryImages = document.querySelectorAll(
			`.gallery-${data.id} .swiper-slide img, 
			.gallery-${data.id} .swiper-slide video`
		);
		for (const element of galleryImages) {
			element.style.maxHeight = `calc(100% - ${maxHeight}px`;
		}
	}

	onMount(() => {
		if (typeof window !== 'undefined') {
			window.addEventListener('resize', updateHeight);
		}

		updateHeight();
	});

	onDestroy(() => {
		if (typeof window !== 'undefined') {
			window.removeEventListener('resize', updateHeight);
		}
	});

	afterUpdate(() => {
		updateHeight();
	});
</script>

<Section class="section-gallery">
	<div class="wrapper">
		<Slider name="gallery-{data.id}" type="multi" pagination={false} {options}>
			{#each slides as slide}
				<div class="swiper-slide" class:aspect={data.keep_aspect_ratio}>
					<Media {...slide.media} fit={data.keep_aspect_ratio ? 'contain' : 'cover'} />
					<div class="description">
						{#if slide.title}
							<div class="title">{slide.title}</div>
						{/if}
						{#if slide.text}
							<div class="text">{slide.text}</div>
						{/if}
					</div>
				</div>
			{/each}
		</Slider>
	</div>
</Section>

<style lang="scss">
	.wrapper {
		width: 100%;

		:global(.swiper-wrapper) {
			height: 50vh;
		}

		:global(img),
		:global(video) {
			overflow: hidden;
			margin-bottom: 16px;
			border-radius: v(border-radius-xs);
		}

		:global(.navigation) {
			margin-top: v(content-padding-sm);
		}

		.swiper-slide {
			display: flex;
			flex-direction: column;

			&.aspect {
				width: auto !important;
				display: block;

				:global(img),
				:global(video),
				:global(.video-player) {
					width: auto;
					height: 100%;
				}
			}
		}

		.description {
			@include font(text-lg);
		}

		.title {
			font-weight: 600;
		}
	}
</style>
